import {
  Box,
  Button,
  Flex,
  Icon,
  Option,
  Select,
  Tag,
  Text,
  Divider,
  Stack,
} from '@tonic-one/react';
import { AddIcon, ArrowDownIcon, DeleteIcon } from '@tonic-one/react-icons';
import isEqual from 'lodash/isEqual';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWhyDidYouUpdate } from 'src/utils/hooks';

import { FormInput } from '../Common/FormComponent';
import { FlexOption } from '../EditableList';
import useHeaderFieldListStyle from './style';
import SmartTooltip from '@Report/SmartTooltip';
import cloneDeep from 'lodash/cloneDeep';
import { FlexList } from '@Components/HeaderFieldList';
import { storeSelector, usePolicyContext } from '@Policy/store';
import { asBlockedHeaderListSelector } from '@Policy/store/filters/advancedSpamProtection';

export interface BlockedHeaderRuleDefinitionsProps {
  key: string;
  operator: string;
  value: string;
}

const RuleDefinitions = (props: {
  maxLength: number;
  disabled: boolean;
  operators: {
    value: string;
    name: string;
  }[];
  limit: number;
  onDelete: (item: BlockedHeaderRuleDefinitionsProps[]) => void;
  onAdd: (item: BlockedHeaderRuleDefinitionsProps[]) => void;
}) => {
  const { maxLength, operators, disabled, onAdd, limit } = props;
  const { t } = useTranslation();
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const blockedHeaderList = contextSelector(
    asBlockedHeaderListSelector,
    isEqual
  );
  const initData: BlockedHeaderRuleDefinitionsProps[] = [
    {
      key: '',
      operator: 'equals',
      value: '',
    },
  ];
  const [data, setData] =
    useState<BlockedHeaderRuleDefinitionsProps[]>(initData);

  const validateInput = ({
    key: field,
    operator,
    value,
  }: BlockedHeaderRuleDefinitionsProps) => {
    if (
      !field ||
      !operator ||
      !value ||
      field.length > maxLength ||
      value.length > maxLength
    ) {
      window.showNotification({
        titleMsg: t(
          'globalSetting.approvedHeaderFields.addError.invalidInput.title'
        ),
        bodyMsg: t(
          'globalSetting.approvedHeaderFields.addError.invalidInput.content',
          { count: maxLength }
        ),
        severity: 'error',
      });
      return false;
    } else {
      return true;
    }
  };

  const reachRulesLimit = () => {
    if (data.length + 1 > limit) {
      window.showNotification({
        titleMsg: t(
          'globalSetting.blockedHeaderFields.addError.reachLimit.title'
        ),
        bodyMsg: t(
          'globalSetting.blockedHeaderFields.addError.reachLimit.content',
          { count: limit }
        ),
        severity: 'error',
      });
      return false;
    } else {
      return true;
    }
  };

  const reachBlockedListLimit = () => {
    if (blockedHeaderList.length + 1 > limit) {
      window.showNotification({
        titleMsg: t(
          'globalSetting.blockedHeaderFields.addRulesError.reachLimit.title'
        ),
        bodyMsg: t(
          'globalSetting.blockedHeaderFields.addRulesError.reachLimit.content',
          { count: limit }
        ),
        severity: 'error',
      });
      return false;
    } else {
      return true;
    }
  };
  function removeLastColon(str: string) {
    return str.replace(/[:\s]+$/, '');
  }

  const checkDuplicateRule = () => {
    if (JSON.stringify(blockedHeaderList).includes(JSON.stringify(data))) {
      window.showNotification({
        titleMsg: t('globalSetting.HeaderFields.duplicate.header'),
        bodyMsg: t('globalSetting.HeaderFields.duplicate.content'),
        severity: 'error',
      });
      return false;
    } else {
      return true;
    }
  };

  const checkDuplicateField = () => {
    const copyData = cloneDeep(data);
    const lastField = copyData.pop();
    if (copyData.some((item) => isEqual(item, lastField))) {
      window.showNotification({
        titleMsg: t('globalSetting.HeaderFields.duplicate.header'),
        bodyMsg: t('globalSetting.HeaderFields.duplicate.content'),
        severity: 'error',
      });
      return false;
    } else {
      return true;
    }
  };

  const addFieldCheck = () => {
    for (const item of data) {
      const tempKey = removeLastColon(item.key);
      if (
        !validateInput({
          key: tempKey,
          operator: item.operator,
          value: item.value,
        }) ||
        !checkDuplicateField() ||
        !reachRulesLimit()
      ) {
        return;
      }
    }
    const copyData = cloneDeep(data);
    copyData.push({
      key: '',
      operator: 'equals',
      value: '',
    });
    setData(copyData);
  };

  const addRuleCheck = () => {
    for (const item of data) {
      const tempKey = removeLastColon(item.key);
      if (
        !validateInput({
          key: tempKey,
          operator: item.operator,
          value: item.value,
        }) ||
        !checkDuplicateRule() ||
        !reachBlockedListLimit()
      ) {
        return;
      }
    }
    onAdd(data);
    setData(initData);
  };
  return (
    <Flex direction="column" width="762px">
      {data.map((item, index) => {
        return (
          <Box key={index}>
            <Stack ml="12px" direction="row" flexWrap="wrap" gap="6x">
              {index === 0 ? (
                <FormInput
                  value={item.key}
                  width="206px"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newData = cloneDeep(data);
                    newData[index].key = e.target.value;
                    setData(newData);
                  }}
                  maxLength={maxLength}
                  placeholder={t('policy.blockedHeaderFields.fieldName')}
                  disabled={disabled}
                ></FormInput>
              ) : (
                <Flex direction="row" width="206px">
                  <Tag mr={8}>{t('common.operator.and')}</Tag>
                  <FormInput
                    value={item.key}
                    width="100%"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const newData = cloneDeep(data);
                      newData[index].key = e.target.value;
                      setData(newData);
                    }}
                    maxLength={maxLength}
                    placeholder={t('policy.blockedHeaderFields.fieldName')}
                    disabled={disabled}
                  ></FormInput>
                </Flex>
              )}
              <Select
                value={item.operator}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const newData = cloneDeep(data);
                  newData[index].operator = e.target.value;
                  setData(newData);
                }}
                width="176px"
                disabled={disabled}
              >
                {operators.map((item) => (
                  <Option value={item.value} key={item.value}>
                    {item.name}
                  </Option>
                ))}
              </Select>
              <FormInput
                width="246px"
                value={item.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const newData = cloneDeep(data);
                  newData[index].value = e.target.value;
                  setData(newData);
                }}
                placeholder={t('policy.blockedHeaderFields.value')}
                disabled={disabled}
                maxLength={maxLength}
              ></FormInput>
              <Button
                disabled={index === 0 || disabled}
                onClick={() => {
                  let newData = cloneDeep(data);
                  newData = newData.filter(
                    (item, itemIndex) => itemIndex !== index
                  );
                  setData(newData);
                }}
                variant="ghost"
              >
                <Icon as={DeleteIcon} />
              </Button>
            </Stack>
            <Divider
              mt="8px"
              mb="8px"
              variant="solid"
              orientation="horizontal"
            ></Divider>
          </Box>
        );
      })}
      <Stack
        direction="row"
        shouldWrapChildren={true}
        spacing="8px"
        ml="6px"
        mt="4px"
      >
        <Button
          borderRadius="2rem"
          columnGap="2x"
          variant="secondary"
          disabled={disabled || false}
          onClick={() => addFieldCheck()}
        >
          <Icon as={AddIcon} />
          {t(
            'policy.approvedAndBlockedLists.blockedHeaderFields.addHeaderField'
          )}
        </Button>
      </Stack>
      <Divider mt="16px" variant="solid" orientation="horizontal" />
      <Stack direction="row" shouldWrapChildren={true} my="16px" pl="4px">
        <Button
          borderRadius="2rem"
          columnGap="2x"
          variant="secondary"
          disabled={disabled || false}
          onClick={() => addRuleCheck()}
        >
          <Icon as={ArrowDownIcon} />
          {t('policy.blockedHeaderFields.addToRule')}
        </Button>
      </Stack>
    </Flex>
  );
};

function BlockedHeaderFieldList(props: {
  onDelete: (item: BlockedHeaderRuleDefinitionsProps[]) => void;
  onAdd: (item: BlockedHeaderRuleDefinitionsProps[]) => void;
  list: any[];
  disabled: boolean;
  limit: number;
  description: string;
  width: string;
  size: number;
  headerMaxLength: number;
  operators: {
    value: string;
    name: string;
  }[];
}) {
  const { descriptionStyle } = useHeaderFieldListStyle();
  const {
    onDelete,
    onAdd,
    list,
    disabled,
    limit,
    description,
    width,
    operators,
    headerMaxLength,
  } = props;
  const { t } = useTranslation();
  const action = React.useMemo(() => {
    return {
      delete: {
        disabled,
        handler: onDelete,
      },
    };
  }, [onDelete, disabled]);
  const prefixRef = React.useRef(Math.random().toString(36).slice(4));
  useWhyDidYouUpdate('BlockedHeaderFieldList', props);
  return (
    <Box>
      <FlexList width={width} direction="column" height="420px">
        <Text ml="12px">{t('policy.blockedHeaderFields.upLabel')}</Text>
        <Flex alignItems="flex-start" mt="12px">
          <RuleDefinitions
            limit={limit}
            onAdd={onAdd}
            maxLength={headerMaxLength}
            disabled={disabled || false}
            operators={operators}
            onDelete={onDelete}
          />
        </Flex>
        <Text>{t('policy.blockedHeaderFields.downLabel')}</Text>
        {description && (
          <Flex>
            <Text style={descriptionStyle}>{description}</Text>
          </Flex>
        )}
        {list.map((item, index) => (
          <FlexOption
            action={action}
            value={item}
            key={`${prefixRef.current}-${index}-${item.value}`}
          >
            {index !== 0 ? (
              <Tag mr={8}>{t('common.operator.or')}</Tag>
            ) : undefined}
            <Text maxWidth={600}>
              <SmartTooltip>
                {item
                  .map(
                    (it: BlockedHeaderRuleDefinitionsProps) =>
                      `${it.key} ${it.operator} ${it.value}`
                  )
                  .join(' ' + t('common.operator.and') + ' ')}
              </SmartTooltip>
            </Text>
          </FlexOption>
        ))}
      </FlexList>
    </Box>
  );
}

export default BlockedHeaderFieldList;
